import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./LetterToShareholdersPage.css";
import { getNewsroomShareholderLetters } from "../services/shareholderLetterService";

const titleWords = ["Letter", "to", "Shareholders"];
const preferredLanguages = [
  {
    languageLabel: "English",
    displayLabel: "English",
    languageSlug: "english",
    translateFromEnglish: false,
  },
  {
    languageLabel: "French",
    displayLabel: "French",
    languageSlug: "english",
    translateFromEnglish: true,
    targetLanguage: "french",
  },
  {
    languageLabel: "Dutch",
    displayLabel: "Dutch",
    languageSlug: "english",
    translateFromEnglish: true,
    targetLanguage: "dutch",
  },
];

const normalizeValue = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase();

const getCreatedTimestamp = (item) => {
  const parsed = new Date(item?.createdAt || 0);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const isPublishedLetter = (item) =>
  normalizeValue(item?.status) === "published";

const LetterToShareholdersPage = () => {
  const location = useLocation();
  const isFromAdmin = Boolean(location.state?.fromAdmin);
  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const [titleScrollOffset, setTitleScrollOffset] = useState(0);
  const [languageLinks, setLanguageLinks] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const selectedLetter = location.state?.selectedLetter || null;
  const backLinkTarget = isFromAdmin ? "/components/Admins/AdminLayout" : "/";
  const backLinkLabel = isFromAdmin ? "Back to AdminLayout" : "Back to Home";
  const currentDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date());

  useEffect(() => {
    const revealTimer = window.setTimeout(() => {
      setIsTitleVisible(true);
    }, 80);

    const handleScroll = () => {
      const scrollY = window.scrollY;
      setTitleScrollOffset(Math.min(scrollY * 0.22, 140));
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    const loadLetters = async () => {
      try {
        setErrorMessage("");
        const res = await getNewsroomShareholderLetters();
        const payload = res?.data ?? [];
        const allLetters = Array.isArray(payload) ? payload : [];
        const latestPublishedLetter =
          allLetters
            .filter(isPublishedLetter)
            .sort(
              (left, right) =>
                getCreatedTimestamp(right) - getCreatedTimestamp(left)
            )[0] || null;
        const activeLetter = selectedLetter || latestPublishedLetter;

        if (activeLetter?.languageSlug) {
          const selectedLanguages = preferredLanguages.map(
            (preferredLanguage) => ({
              ...activeLetter,
              displayLabel: preferredLanguage.displayLabel,
              languageLabel: preferredLanguage.languageLabel,
              translateFromEnglish: preferredLanguage.translateFromEnglish,
              targetLanguage: preferredLanguage.targetLanguage,
            })
          );

          setLanguageLinks(selectedLanguages);
          return;
        }

        const englishLetter = allLetters.find((item) => {
          const currentLabel = normalizeValue(item.languageLabel);
          const currentSlug = normalizeValue(item.languageSlug);

          return (
            currentLabel.includes("english") ||
            currentSlug.includes("english")
          );
        });

        const mergedLanguages = preferredLanguages.map((preferredLanguage) => {
          if (preferredLanguage.translateFromEnglish) {
            return englishLetter
              ? {
                  ...englishLetter,
                  languageLabel: preferredLanguage.languageLabel,
                  targetLanguage: preferredLanguage.targetLanguage,
                  translateFromEnglish: true,
                }
              : preferredLanguage;
          }

          const preferredLabel = normalizeValue(preferredLanguage.languageLabel);
          const preferredSlug = normalizeValue(preferredLanguage.languageSlug);
          const matchingLetter = allLetters.find((item) => {
            const currentLabel = normalizeValue(item.languageLabel);
            const currentSlug = normalizeValue(item.languageSlug);

            return (
              currentLabel === preferredLabel ||
              currentSlug === preferredSlug ||
              currentLabel.includes(preferredLabel) ||
              currentSlug.includes(preferredSlug)
            );
          });

          return matchingLetter
            ? {
                ...matchingLetter,
                displayLabel: preferredLanguage.displayLabel,
              }
            : preferredLanguage;
        });

        setLanguageLinks(mergedLanguages);
      } catch (error) {
        console.error("Failed to load shareholder letter languages:", error);
        setErrorMessage("Unable to load shareholder letters right now.");
        setLanguageLinks([]);
      }
    };

    loadLetters();

    return () => {
      window.clearTimeout(revealTimer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [selectedLetter]);

  const titleStyle = {
    opacity: isTitleVisible ? 1 : 0,
    transform: isTitleVisible
      ? `translate3d(0, ${-titleScrollOffset * 0.3}px, 0)`
      : "translate3d(0, 60px, 0)",
    transition:
      "opacity 900ms ease 120ms, transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 120ms",
  };

  const dateStyle = {
    opacity: isTitleVisible ? 1 : 0,
    transform: isTitleVisible
      ? `translate3d(0, ${18 - titleScrollOffset * 0.42}px, 0)`
      : "translate3d(0, 88px, 0)",
    transition:
      "opacity 900ms ease 260ms, transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 260ms",
  };

  return (
    <main className="shareholders-page">
      <div className="shareholders-home-link-wrap">
        <Link to={backLinkTarget} className="shareholders-home-link">
          &larr; {backLinkLabel}
        </Link>
      </div>

      <section className="shareholders-hero">
        <h1 className="shareholders-title">
          <span
            className={`shareholders-title-text shareholders-title-text-stacked${
              isTitleVisible ? " is-visible" : ""
            }`}
            style={titleStyle}
            aria-label="Letter to shareholders"
          >
            <span className="shareholders-title-line">
              {titleWords.slice(0, 2).map((word, wordIndex) => {
                const letterOffset = titleWords
                  .slice(0, wordIndex)
                  .reduce((count, currentWord) => count + currentWord.length, 0);

                return (
                  <span
                    key={`${word}-${wordIndex}`}
                    className="shareholders-title-word"
                  >
                    {word.split("").map((letter, letterIndex) => (
                      <span
                        key={`${word}-${letterIndex}`}
                        className="shareholders-title-letter"
                        style={{
                          "--reveal-order": letterOffset + letterIndex + 1,
                        }}
                      >
                        {letter}
                      </span>
                    ))}
                  </span>
                );
              })}
            </span>

            <span className="shareholders-title-line">
              {titleWords.slice(2).map((word, wordIndex) => {
                const letterOffset =
                  titleWords[0].length + titleWords[1].length + wordIndex;

                return (
                  <span
                    key={`${word}-${wordIndex}`}
                    className="shareholders-title-word"
                  >
                    {word.split("").map((letter, letterIndex) => (
                      <span
                        key={`${word}-${letterIndex}`}
                        className="shareholders-title-letter"
                        style={{
                          "--reveal-order": letterOffset + letterIndex + 1,
                        }}
                      >
                        {letter}
                      </span>
                    ))}
                  </span>
                );
              })}
            </span>
          </span>
        </h1>

        <p className="shareholders-date" style={dateStyle}>
          {currentDate}
        </p>
      </section>

      <section className="shareholders-content">
        <div className="shareholders-copy">
          <p>
            Read the letter from CEO{" "}
            <a
              href="https://www.linkedin.com/in/blackroth-blackroth"
              target="_blank"
              rel="noreferrer"
            >
              Karthik Nakka:
            </a>
          </p>
        </div>

        <div className="shareholders-languages">
          {languageLinks.map((item, index) =>
            item.id ? (
              <Link
                key={`${item.languageSlug}-${item.languageLabel}-${index}`}
                to={`/letter-to-shareholders/${
                  selectedLetter?.languageSlug || item.languageSlug
                }${
                  selectedLetter
                    ? `?source=newsroom${
                        item.translateFromEnglish
                          ? `&translate=${item.targetLanguage}`
                          : ""
                      }`
                    : `${
                        item.translateFromEnglish
                          ? `?translate=${item.targetLanguage}`
                          : ""
                      }`
                }`}
                state={location.state}
                className="shareholders-language-link"
              >
                <span>{item.displayLabel || item.languageLabel}</span>
                <span className="shareholders-arrow" aria-hidden="true">
                  &rarr;
                </span>
              </Link>
            ) : (
              <span
                key={`${item.languageSlug}-${item.languageLabel}-${index}`}
                className="shareholders-language-link is-disabled"
              >
                <span>{item.displayLabel || item.languageLabel}</span>
                <span className="shareholders-arrow" aria-hidden="true">
                  &rarr;
                </span>
              </span>
            )
          )}

          {!languageLinks.length && (
            <p className="shareholders-copy">
              {errorMessage || "No shareholder letters have been created yet."}
            </p>
          )}
        </div>
      </section>
    </main>
  );
};

export default LetterToShareholdersPage;